import React               from 'react';
import { isNullOrUndefined }    from 'rewire-common';
import { observable, Observe }  from 'rewire-core';
import { Modal, Form }          from 'rewire-ui';
import { Theme, makeStyles }    from '../utility/styles';
import Typography               from '@material-ui/core/Typography';
import { Deferred }             from '../utility/Deferred';
import DefaultEditFormView      from './DefaultEditFormView';
import DraggableResizableDialog from '../ui/DraggableResizableDialog';

export interface ISettingsParams {
  title:          string;
  subTitle?:      string;
  buttons?:       string[];
  defaultButton?: string;
  form:           Form;
  defaultWidth?:  'xs' | 'sm' | 'md' | 'lg' | number | string;
  minWidth?:      number | string;
  minHeight?:     number | string;
}

export class SettingsDialogModel extends Modal<{buttons: string[], defaultWidth: 'xs' | 'sm' | 'md' | 'lg' | number | string, minWidth: number | string, minHeight: number | string}> {
  subTitle?:      string | JSX.Element;
  form:           Form;
  defaultButton?: string;
  __result?:      string;
  __deferred:     Deferred<any> | undefined;

  constructor() {
    super('', false);
    this.state.buttons      = ['ok'];
    this.state.defaultWidth = 'xs';
    this.state.minWidth     = '275px';
    this.state.minHeight    = '216px';
    this.action('save',   this.submit.bind(this, 'save'),          { icon: 'save', variant: 'contained', color: 'primary', disabled: () => this.form.hasErrors || !this.form.hasChanges, type: 'submit' })
        .action('ok',     this.submit.bind(this, 'ok'),            { variant: 'contained', color: 'primary', disabled: () => this.form.hasErrors || !this.form.hasChanges, type: 'submit' })
        .action('apply',  this.submit.bind(this, 'apply'),         { variant: 'contained', color: 'primary', disabled: () => this.form.hasErrors || !this.form.hasChanges, type: 'submit' })
        .action('undo',   this.submit.bind(this, 'undo'),          { variant: 'contained', disabled: () => !this.form.hasChanges })
        .action('delete', this.submit.bind(this, 'delete') as any, { variant: 'contained', icon: 'delete' })
        .action('close',  this.submit.bind(this, 'close'),         { icon: 'cancel' })
        .action('cancel', this.submit.bind(this, 'cancel'),        { icon: 'cancel' });
  }

  get visible() { return true; }

  load(params: ISettingsParams) {
    this.state.defaultWidth  = !isNullOrUndefined(params.defaultWidth) ? params.defaultWidth : 'xs';
    this.state.minWidth      = !isNullOrUndefined(params.minWidth) ? params.minWidth : '290px';
    this.state.minHeight     = !isNullOrUndefined(params.minHeight) ? params.minHeight : '216px';
    this.state.title         = params.title ?? 'Confirm';
    this.state.buttons.set(params.buttons ?? ['close']);
    this.form                = params.form;
    this.defaultButton       = params.defaultButton;
    this.__deferred          = new Deferred();
    super.open();
    return this.__deferred.promise;
  }

  get buttons() {
    return this.state.buttons;
  }

  get defaultWidth() {
    return this.state.defaultWidth;
  }

  get minWidth() {
    return this.state.minWidth;
  }

  get minHeight() {
    return this.state.minHeight;
  }

  submit(action: string) {
    this.__result = action;
    return Promise.resolve(true);
  }

  close = () => {
    this.state.open = false;
    this.__deferred?.resolve(this.__result);
  };
}

export const settingsDialogModel = observable(new SettingsDialogModel());

const useSettingsDialogStyle = makeStyles((_theme: Theme) => ({
  title: {
    whiteSpace: 'pre-line',
  },
  subTitle: {
    marginTop: '10px',
  },
}));

export default function SettingsDialog() {
  const classes = useSettingsDialogStyle();
  return <Observe render={() => {
    const { defaultWidth, minWidth, minHeight, subTitle, buttons, defaultButton } = settingsDialogModel;

    return (
      <DraggableResizableDialog defaultWidth={defaultWidth} DialogProps={{dialog: settingsDialogModel, actions: buttons}} DraggableResizableBoxProps={{minWidth: minWidth, minHeight: minHeight}}>
        {subTitle && <Typography className={classes.subTitle} variant='subtitle1'>{subTitle}</Typography> }
        <DefaultEditFormView editor={settingsDialogModel} submit={defaultButton}/>
      </DraggableResizableDialog>
    );
  }} />;
}
